import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { Html5Qrcode } from 'html5-qrcode'
import { LoadingSmall } from '../pages/loading/loadingPage'

const QRCodeReader = ({ open, onClose, onScan }) => {
  const [regionReady, setRegionReady] = useState(false)
  const [starting, setStarting] = useState(false)
  const [errorText, setErrorText] = useState('')

  const regionIdRef = useRef(`qr-region-${Math.random().toString(16).slice(2)}`)
  const qrRef = useRef(null)

  // callback ref so we KNOW when the element exists
  const setRegionNode = (node) => {
    setRegionReady(!!node)
  }

  useEffect(() => {
    if (!open) return
    if (!regionReady) return

    let cancelled = false
    setStarting(true)
    setErrorText('')

    const start = async () => {
      try {
        const regionId = regionIdRef.current
        const qr = new Html5Qrcode(regionId)
        qrRef.current = qr

        const devices = await Html5Qrcode.getCameras()
        if (!devices?.length) throw new Error('No cameras found')

        const preferred =
          devices.find((d) => /back|rear|environment/i.test(d.label))?.id ||
          devices[0].id

        if (cancelled) return

        await qr.start(
          { deviceId: { exact: preferred } },
          { fps: 10, qrbox: 250, aspectRatio: 1 },
          (decodedText) => {
            onScan(decodedText)
            onClose()
          },
          () => {} // ignore per-frame scan failures
        )

        if (!cancelled) setStarting(false)
      } catch (e) {
        if (cancelled) return
        setStarting(false)
        setErrorText(`${e?.name ?? 'Error'}: ${e?.message ?? String(e)}`)
        console.error('html5-qrcode start failed:', e)
      }
    }

    start()

    return () => {
      cancelled = true
      ;(async () => {
        try {
          const qr = qrRef.current
          if (qr) {
            try {
              await qr.stop()
            } catch {}
            try {
              await qr.clear()
            } catch {}
          }
        } finally {
          qrRef.current = null
        }
      })()
    }
  }, [open, regionReady, onClose, onScan])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography fontSize="1rem" textAlign="center">
          Scan QR Code
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack gap={1}>
          <Box
            sx={{
              width: '100%',
              height: 340,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
              bgcolor: 'black',
            }}
          >
            {starting && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.8)',
                }}
              >
                <LoadingSmall />
              </Box>
            )}

            <div
              ref={setRegionNode}
              id={regionIdRef.current}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>

          {errorText && (
            <Typography color="error" variant="body2">
              {errorText}
            </Typography>
          )}

          <Button variant="contained" color="anRed" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default QRCodeReader
